import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Plus, Loader2, Clock, X, CalendarClock, CheckCheck } from "lucide-react";
import { API, authHeaders } from "@/constants/roles";

const PRIORITY_STYLES = {
  urgent: "bg-rose-500/15 text-rose-300 border-rose-500/30",
  high: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  normal: "bg-[var(--pv-subtle)] text-[var(--pv-text-3)] border-[var(--pv-border)]",
  low: "bg-[var(--pv-subtle)] text-[var(--pv-text-4)] border-[var(--pv-border)]",
};
const inputCls =
  "rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-bg)] px-4 py-2.5 text-sm font-medium text-[var(--pv-text)] placeholder:text-[var(--pv-text-4)] focus:border-cyan-400 focus:outline-none focus:ring-4 focus:ring-cyan-500/10 transition-all";

// Shared daily command-center checklist (owner + manager get role-appropriate tasks from the backend)
export const TasksPanel = ({ onNavigate, testid = "tasks-panel", title = "Today's command list" }) => {
  const [data, setData] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ title: "", detail: "", priority: "normal", due_date: "", recurrence: "" });
  const [busyId, setBusyId] = useState(null);
  const [confirmClear, setConfirmClear] = useState(false);
  const [clearing, setClearing] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/staff/tasks`, { headers: authHeaders() })
      .then((r) => setData(r.data))
      .catch(() => {});
  }, []);

  useEffect(load, [load]);

  const act = async (task, action, extra = {}) => {
    setBusyId(task.id || task.source_key);
    try {
      await axios.patch(`${API}/staff/tasks/${task.id || task.source_key}`, { action, ...extra }, { headers: authHeaders() });
      load();
    } catch {
      toast.error("Could not update task.");
    } finally {
      setBusyId(null);
    }
  };

  const add = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/staff/tasks`, {
        title: form.title, detail: form.detail, priority: form.priority,
        due_date: form.due_date || null, recurrence: form.recurrence || null,
      }, { headers: authHeaders() });
      toast.success(form.recurrence ? `Recurring task added (${form.recurrence}).` : "Task added.");
      setShowAdd(false);
      setForm({ title: "", detail: "", priority: "normal", due_date: "", recurrence: "" });
      load();
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Could not add task.");
    }
  };

  const clearAll = async () => {
    setClearing(true);
    try {
      const { data: res } = await axios.post(`${API}/staff/tasks/clear-all`, {}, { headers: authHeaders() });
      toast.success(res.cleared ? `Cleared ${res.cleared} task${res.cleared === 1 ? "" : "s"}.` : "List cleared.");
      setConfirmClear(false);
      load();
    } catch {
      toast.error("Could not clear the list.");
    } finally {
      setClearing(false);
    }
  };

  return (
    <div data-testid={testid} className="rounded-3xl border border-[var(--pv-border)] bg-[var(--pv-card)] p-6 sm:p-8">
      <div className="flex items-center justify-between gap-3">
        <h2 className="font-display text-lg font-bold text-[var(--pv-text)]">{title}</h2>
        <div className="flex items-center gap-2">
          {(data?.tasks?.length || 0) > 0 && (
            confirmClear ? (
              <span data-testid="task-clear-confirm" className="flex items-center gap-2 rounded-full border border-rose-500/30 bg-rose-500/10 px-3 py-1.5">
                <span className="text-[11px] font-bold text-rose-300">Clear {data.tasks.length} task{data.tasks.length === 1 ? "" : "s"}?</span>
                <button data-testid="task-clear-confirm-btn" onClick={clearAll} disabled={clearing} className="rounded-full bg-rose-500 px-2.5 py-1 text-[11px] font-bold text-white hover:bg-rose-600 disabled:opacity-60">
                  {clearing ? <Loader2 className="h-3 w-3 animate-spin" /> : "Yes, clear"}
                </button>
                <button data-testid="task-clear-cancel-btn" onClick={() => setConfirmClear(false)} className="text-[11px] font-bold text-[var(--pv-text-3)]">Cancel</button>
              </span>
            ) : (
              <button data-testid="task-clear-all-btn" onClick={() => setConfirmClear(true)} className="flex items-center gap-1.5 rounded-full border border-[var(--pv-border)] bg-[var(--pv-subtle)] px-3.5 py-2 text-xs font-bold text-[var(--pv-text-2)] hover:text-rose-400">
                <CheckCheck className="h-3.5 w-3.5" /> Clear all
              </button>
            )
          )}
          <button data-testid="task-add-btn" onClick={() => setShowAdd((s) => !s)} className="flex items-center gap-1.5 rounded-full border border-[var(--pv-border)] bg-[var(--pv-subtle)] px-3.5 py-2 text-xs font-bold text-[var(--pv-text-2)] hover:text-cyan-400">
            {showAdd ? <X className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />} {showAdd ? "Close" : "Add task"}
          </button>
        </div>
      </div>

      {showAdd && (
        <form data-testid="task-add-form" onSubmit={add} className="mt-4 grid gap-3 rounded-2xl border border-cyan-500/20 bg-[var(--pv-subtle)] p-4 sm:grid-cols-2">
          <input data-testid="task-title-input" required minLength={2} value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="Task title" className={`${inputCls} sm:col-span-2`} />
          <input data-testid="task-detail-input" value={form.detail} onChange={(e) => setForm((f) => ({ ...f, detail: e.target.value }))} placeholder="Detail (optional)" className={inputCls} />
          <input data-testid="task-due-input" type="date" value={form.due_date} onChange={(e) => setForm((f) => ({ ...f, due_date: e.target.value }))} className={inputCls} />
          <select data-testid="task-priority-input" value={form.priority} onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))} className={inputCls}>
            <option value="low">Low priority</option><option value="normal">Normal priority</option><option value="high">High priority</option><option value="urgent">Urgent</option>
          </select>
          <select data-testid="task-recurrence-input" value={form.recurrence} onChange={(e) => setForm((f) => ({ ...f, recurrence: e.target.value }))} className={inputCls}>
            <option value="">One-time task</option><option value="daily">Repeats daily</option><option value="weekly">Repeats weekly</option>
          </select>
          <button data-testid="task-save-btn" type="submit" className="flex w-fit items-center gap-1.5 rounded-full bg-cyan-500 px-5 py-2.5 text-xs font-bold text-white hover:bg-cyan-600 sm:col-span-2">
            <Plus className="h-3.5 w-3.5" /> Add task
          </button>
        </form>
      )}

      {!data ? (
        <p data-testid="tasks-loading" className="py-8 text-center text-sm text-[var(--pv-text-4)]"><Loader2 className="mx-auto h-5 w-5 animate-spin text-cyan-400" /></p>
      ) : data.caught_up ? (
        <div data-testid="tasks-caught-up" className="flex flex-col items-center gap-2 py-10 text-center">
          <CheckCheck className="h-8 w-8 text-emerald-400" />
          <p className="font-display text-lg font-bold text-[var(--pv-text)]">You're caught up ✓</p>
          <p className="text-xs text-[var(--pv-text-4)]">Nothing needs your attention right now.</p>
        </div>
      ) : (
        <div className="mt-4 space-y-2">
          {data.tasks.map((t) => {
            const key = t.id || t.source_key;
            return (
              <div key={key} data-testid={`task-row-${key}`} className="flex flex-wrap items-center gap-3 rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-subtle)] px-4 py-3">
                <button
                  data-testid={`task-complete-${key}`}
                  onClick={() => t.kind === "system" ? act(t, "dismiss") : act(t, "complete")}
                  disabled={busyId === key}
                  title={t.kind === "system" ? "Dismiss" : "Mark complete"}
                  className="shrink-0 text-[var(--pv-text-4)] transition-colors hover:text-emerald-400 disabled:opacity-50"
                >
                  {busyId === key ? <Loader2 className="h-4.5 w-4.5 animate-spin" /> : <CheckCircle2 className="h-5 w-5" />}
                </button>
                <div className="min-w-0 flex-1">
                  <button onClick={() => t.link && onNavigate?.(t.link)} className={`text-left text-sm font-semibold ${t.link ? "text-[var(--pv-text)] hover:text-cyan-400" : "text-[var(--pv-text)]"}`}>
                    {t.title}
                  </button>
                  <p className="text-[11px] text-[var(--pv-text-4)]">
                    {[t.detail, t.due_date ? `due ${t.due_date}` : null, t.recurrence ? `repeats ${t.recurrence}` : null, t.kind === "system" ? "auto" : null].filter(Boolean).join(" · ")}
                  </p>
                </div>
                <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase ${PRIORITY_STYLES[t.priority] || PRIORITY_STYLES.normal}`}>{t.priority}</span>
                <button data-testid={`task-snooze-${key}`} onClick={() => act(t, "snooze", { snooze_days: 1 })} title="Snooze 1 day" className="shrink-0 rounded-full p-1.5 text-[var(--pv-text-4)] hover:bg-[var(--pv-card)] hover:text-amber-400">
                  <Clock className="h-3.5 w-3.5" />
                </button>
                {t.kind !== "system" && (
                  <button data-testid={`task-dismiss-${key}`} onClick={() => act(t, "dismiss")} title="Dismiss" className="shrink-0 rounded-full p-1.5 text-[var(--pv-text-4)] hover:bg-[var(--pv-card)] hover:text-rose-400">
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
